import { useEffect, useState } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import type { Crop } from 'react-image-crop';
import { tokens } from '@hotjar/hotsauce';

import styled from '@emotion/styled';
import { type CanvasRef, getLayersImageData } from '@hotjar/heatmaps';
import { WIDGET_Z_INDEX } from '@hotjar/webapp/features/Heatmaps/ContinuousCaptureReport/ReportWidgets/constants';
import { useNotify } from '@hotjar/webapp/hooks/useNotify';
import { AvatarsOnHeatmap } from '@hotjar/webapp/features/Highlights/Heatmaps/AvatarsOnHeatmap';

import { CreateHighlightInfoBar } from './CreateHighlightInfoBar';
import { HighlightAreaSelector } from './HighlightAreaSelector';
import { HighlightCreator as HighlightCreatorBase } from '../HighlightCreator';

import type { HighlightedRegion } from './use-highlights-state';
import { useHeatmapHighlightCreate } from './use-highlight-create';

interface ContainerProps {
  top?: number;
  left?: number;
  width?: number;
  height?: number;
}

const HighlightCreatorContainer = styled.div<{ left: number; width: number }>`
  position: absolute;
  left: 0;
  top: 0;
  width: ${({ width }) => width}px;
  left: ${({ left }) => left}px;
  z-index: 1000;
`;

const DescriptionCreatorContainer = styled.div<ContainerProps>`
  position: absolute;
  background-color: ${tokens.color.white};
  padding: 0 ${tokens.spacer.x1pt5};
  border-radius: ${tokens.radius.m};
  /* This is so that the insight creation dialog is above the click count and heatmap help widgets */
  z-index: ${WIDGET_Z_INDEX + 9};
  top: ${({ top = 0 }) => top}px;
  left: ${({ left = 0 }) => left}px;
`;

const HighlightCreatorButtons = styled(HighlightCreatorBase)`
  margin: 0;
  padding: ${tokens.spacer.x0pt5} 0 ${tokens.spacer.x1pt5};
`;

const MINIMUM_SELECT_DIMENSIONS = 50;

export interface HighlightCreatorProps {
  canvasRef: React.RefObject<CanvasRef>;
  highlightedRegion: HighlightedRegion | null;
  currentSiteId: string | null;
  height: number;
  isActiveHighlightingEnabled: boolean;
  onCancel: () => void;
  onSave?: () => void;
  screenshotSourceUrl: string;
  width: number;
  filters: Record<string, unknown>;
}

const DIALOG_OFFSET = 16;

export const DIALOG_WIDTH = 342;

export const DIALOG_HEIGHT = 56;

const getPosition = (crop: Required<Crop>): { x: number; y: number } => {
  const containerBBox = document.querySelector('.ReactCrop')?.getBoundingClientRect();

  if (containerBBox === undefined) {
    return { x: 0, y: 0 };
  }

  const containerWidth = containerBBox.width;
  let { x = 0, y = 0 } = crop;

  x += (crop.width - DIALOG_WIDTH) / 2;
  y += crop.height + DIALOG_OFFSET;

  // If the dialog is going to flow off the left of the heatmap we need to move it.
  if (x < 0) {
    x = 0;
  }

  // If the dialog will now flow off the right edge (due to a selection that is shorter than dialog)
  if (x + DIALOG_WIDTH > containerWidth) {
    x = containerWidth - DIALOG_WIDTH;
  }

  const dialogBottomPosition = crop.y + crop.height + DIALOG_HEIGHT + DIALOG_OFFSET;

  // If the dialog will flow off the bottom of the heatmap we need to nudge it up into the screen.
  if (dialogBottomPosition > containerBBox.height) {
    y = crop.y - DIALOG_HEIGHT - DIALOG_OFFSET;
  }

  // If the dialog will now flow off the top of the heatmap we need to nudge it down into the screen and into the selection.
  if (y < 0) {
    y = DIALOG_OFFSET;
  }

  return { x, y };
};

const INITIAL_CROP: Required<Crop> = { x: 0, y: 0, width: 0, height: 0, aspect: 0, unit: 'px' };

function ensureCropValues(crop: Crop, unit: 'px' | '%' = 'px'): Required<Crop> {
  return {
    x: crop.x ?? 0,
    y: crop.y ?? 0,
    width: crop.width ?? 0,
    height: crop.height ?? 0,
    aspect: crop.aspect ?? 0,
    unit: crop.unit ?? unit,
  };
}

export const HighlightCreator = ({
  width,
  height,
  canvasRef,
  highlightedRegion,
  currentSiteId,
  isActiveHighlightingEnabled,
  onCancel,
  onSave,
  screenshotSourceUrl,
  filters,
}: HighlightCreatorProps): JSX.Element | null => {
  const { width: getCanvasWidth = () => width, height: getCanvasHeight = () => height } =
    canvasRef.current ?? {};
  const canvasLeftOffset = canvasRef.current?.left() ?? 0;
  const { t } = useTranslation();
  const [dialogPosition, setDialogPosition] = useState({ x: 0, y: 0 });
  const [crop, setCrop] = useState<Required<Crop>>(INITIAL_CROP);
  const [percentageCrop, setPercentageCrop] = useState<Required<Crop>>(INITIAL_CROP);
  const [selectedHighlightId, setSelectedHighlightId] = useState<string | null>(null);
  const notify = useNotify();

  const { isLoading, mutateAsync: createHighlight } = useHeatmapHighlightCreate(currentSiteId);

  // Reset creating or viewing highlight on toggle highlights on heatmap off
  useEffect(() => {
    if (
      (!isActiveHighlightingEnabled && selectedHighlightId) ||
      (!isActiveHighlightingEnabled && crop !== INITIAL_CROP)
    ) {
      setSelectedHighlightId(null);
      setCrop(INITIAL_CROP);
    }
  }, [isActiveHighlightingEnabled, crop, selectedHighlightId]);

  useEffect(() => {
    if (!highlightedRegion) {
      setCrop(INITIAL_CROP);

      return;
    }

    // calculate x and y coords if width or height are less than minimal selection size
    // so that its centered on the x / y axis

    const adjustedX =
      highlightedRegion.width < MINIMUM_SELECT_DIMENSIONS
        ? highlightedRegion.left - MINIMUM_SELECT_DIMENSIONS / 2
        : highlightedRegion.left;
    const adjustedY =
      highlightedRegion.height < MINIMUM_SELECT_DIMENSIONS
        ? highlightedRegion.top - MINIMUM_SELECT_DIMENSIONS / 2
        : highlightedRegion.top;
    const adjustedWidth =
      highlightedRegion.width < MINIMUM_SELECT_DIMENSIONS
        ? MINIMUM_SELECT_DIMENSIONS
        : highlightedRegion.width;
    const adjustedHeight =
      highlightedRegion.height < MINIMUM_SELECT_DIMENSIONS
        ? MINIMUM_SELECT_DIMENSIONS
        : highlightedRegion.height;

    setCrop({
      x: adjustedX,
      y: adjustedY,
      width: adjustedWidth,
      height: adjustedHeight,
      aspect: 0,
      unit: 'px',
    });
  }, [highlightedRegion]);

  const saveHighlight = async () => {
    const absoluteCropWidth = (percentageCrop.width / 100) * width;
    const absoluteCropHeight = (percentageCrop.height / 100) * height;
    const absoluteX = (percentageCrop.x / 100) * width;
    const absoluteY = (percentageCrop.y / 100) * height;

    createHighlight({
      type: 'heatmap',
      filters,
      selectedArea: {
        x1: absoluteX,
        x2: absoluteX + absoluteCropWidth,
        y1: absoluteY,
        y2: absoluteY + absoluteCropHeight,
      },
      screenshotUrl: screenshotSourceUrl,
      image: getLayersImageData(0.8) ?? '',
      clicksCount: highlightedRegion?.clicksCount ?? null,
      clicksPercentage: highlightedRegion?.clicksPercentage ?? null,
    })
      .then((response) => {
        setSelectedHighlightId(String(response.data.id));

        setCrop(INITIAL_CROP);

        notify.success(t('highlights:save.success'));

        if (onSave) {
          onSave();
        }
      })
      .catch(() => {
        notify.error(t('highlights:save.error'));
      });
  };

  const onCropChange = (newCrop: Crop, newPercentageCrop: Crop) => {
    const checkedCrop = ensureCropValues(newCrop);
    const checkedPercentageCrop = ensureCropValues(newPercentageCrop, '%');

    setCrop(checkedCrop);
    setPercentageCrop(checkedPercentageCrop);
    setDialogPosition(getPosition(checkedCrop));
  };

  if (!isActiveHighlightingEnabled) {
    return null;
  }

  return (
    <>
      <HighlightCreatorContainer left={canvasLeftOffset} width={getCanvasWidth()}>
        <AvatarsOnHeatmap
          canvasRef={canvasRef}
          filters={filters}
          siteId={currentSiteId ?? undefined}
          heatmapWidth={width}
          heatmapHeight={height}
          selectedHighlightId={selectedHighlightId}
          setSelectedHighlightId={setSelectedHighlightId}
        />
        {!!crop.width && (
          <DescriptionCreatorContainer top={dialogPosition.y} left={dialogPosition.x}>
            <HighlightCreatorButtons
              newHighlightTime={null}
              onNewHighlightTime={null}
              onSave={saveHighlight}
              isSubmitting={isLoading}
              onClose={() => {
                onCancel();
                setCrop(INITIAL_CROP);
              }}
            />
          </DescriptionCreatorContainer>
        )}
        <HighlightAreaSelector
          width={getCanvasWidth()}
          height={getCanvasHeight()}
          onChange={onCropChange}
          crop={crop}
        />
      </HighlightCreatorContainer>
      <CreateHighlightInfoBar leftOffset={canvasLeftOffset} />
    </>
  );
};
